<template lang="pug">
.view-part.flex.column.flex-1
  .controller.flex.align-center.gap-6.font-size-20
    .year.bold {{ year }}
    .month.bold {{ month }} 月
    el-button(icon="el-icon-arrow-left" circle, @click="prevWeek")
    el-button(icon="el-icon-arrow-right" circle, @click="nextWeek")
    el-button(round, @click="thisWeek") 今日
    .filter.flex.align-center.gap-6
      slot(name="filter")
    el-select.branch-filter(
      v-if="branchOptions.length > 0"
      v-model="branchFilter"
      clearable
      :placeholder="msg.branchFilter.placeholder")
      el-option(v-for="b in branchOptions", :key="b.id", :label="b.name", :value="b.id")
    .availability-hint.flex.align-center.gap-6(v-if="availability")
      span.unconfigured(v-if="!configured") {{ msg.hint.unconfigured }}
      .legend.flex.align-center.gap-6
        span.legend-item.flex.align-center(v-if="configured")
          i.legend-box.available
          | {{ msg.legend.available }}
        span.legend-item.flex.align-center(v-if="configured")
          i.legend-box.outside
          | {{ msg.legend.outside }}
        span.legend-item.flex.align-center
          i.legend-box.off
          | {{ msg.legend.off }}
        span.legend-item.holiday-text {{ msg.legend.holiday }}
  .empty.flex.align-center.justify-center.flex-1(v-if="emptyText") {{ emptyText }}
  .view-table.flex.column(v-else, ref="table", :class="{'has-slots': configured}")
    .header.flex.column
      .tr.flex.align-stretch
        .th.time &nbsp;
        .th.room.flex.column.justify-center.align-start(
          v-for="(d, i) in dates", :key="d.text")
          el-tooltip(v-if="dayInfos[i].isHoliday"
            :content="dayInfos[i].holidayName", placement="bottom")
            span.date-text.is-holiday {{d.text}}
          span.date-text(v-else) {{d.text}}
          el-tooltip(v-if="dayInfos[i].isUnavailable"
            :content="dayInfos[i].reason || msg.legend.off", placement="bottom")
            el-tag.day-tag(type="info", size="mini") {{ msg.legend.off }}
          el-tooltip(v-else-if="dayInfos[i].partialOff.length > 0"
            :content="partialOffText(dayInfos[i])", placement="bottom")
            el-tag.day-tag(type="info", size="mini") {{ msg.legend.partialOff }}
    .body.flex.column
      template(v-for="hour in hours")
        .tr.flex.align-stretch(:key="`up-${hour.value}`", :data-hour="hour.value")
          .td.time.flex.align-center {{ hour.text }}
          .td.room(v-for="(d, i) in dates", :key="d.text", :class="{'is-off': dayInfos[i].isOff}")
        .tr.flex.align-stretch(:key="`down-${hour.value}`")
          .td.time
          .td.room(v-for="(d, i) in dates", :key="d.text", :class="{'is-off': dayInfos[i].isOff}")
    template(v-for="slot in availableSlots")
      el-tooltip(:key="slot.key", :disabled="!slot.branchText", :content="slot.branchText"
        placement="top", :open-delay="300")
        .available-slot(:style="slot.style", :class="{ dimmed: slot.dimmed }")
          .branch-text(v-if="slot.branchText") {{ slot.branchText }}
    template(v-for="block in partialOffBlocks")
      .partial-off(:key="block.key", :style="block.style")
    template(v-for="activity in activities")
      calendar-block(:data="activity", is-activity, @showActivity="showActivity")
    template(v-for="data in reserveList")
      calendar-block(:data="data" @showPatient="showPatient", @showDetail="showDetail")
    template(v-for="data in occupiedList")
      calendar-block(:data="data", is-occupied)
</template>

<script>
import { getAllAppointments } from '@/api/reserve';
import { getActivities } from '@/api/activity';
import { getBranches } from '@/api/place';
import AppointmentDetail from '../AppointmentDetail.vue';
import CalendarBlock from './CalendarBlock.vue';
import msg from '@/util/msg';
import {
  buildDayInfo, describePartialOff, hasWeeklySlots, jsDayToWeekday,
} from '@/util/availability';

export default {
  name: 'calendar-view',
  components: {
    'appointment-detail': AppointmentDetail,
    CalendarBlock,
  },
  props: {
    hour: {
      type: Number,
      default: 9,
    },
    /** 指定專業人士時，只撈該專業人士的預約與活動 */
    professionalId: {
      type: [Number, String],
      default: undefined,
    },
    /**
     * 自訂載入函式，收到 { start_at, end_at, professional_id }，
     * 回傳 { appointments, occupied, activities }；未指定時分別呼叫預約與活動列表 API
     */
    loader: {
      type: Function,
      default: undefined,
    },
    /** 有值時不撈資料，改為顯示此提示文字 */
    emptyText: {
      type: String,
      default: '',
    },
    /** 專業人士可排案時段設定（後端 availability）；null 時不畫時段與不可排案日期 */
    availability: {
      type: Object,
      default: null,
    },
    /** 國定假日 [{ date: 'YYYY-MM-DD', name }]，涵蓋目前顯示的週 */
    holidays: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const hours = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let shift = today.getDay();
    if (shift === 0) {
      shift = 7;
    }
    shift -= 1;
    const start = new Date(today.getTime() - shift * 86400 * 1000);

    const dates = this.getDatesFrom(start);
    for (let i = 0; i < 24; i += 1) {
      hours.push({
        text: `${i % 12 === 0 ? 12 : i % 12} ${i < 12 ? 'AM' : 'PM'}`,
        value: i,
      });
    }
    return {
      reserveList: [],
      /** 沒有權限檢視的預約，只有時段 */
      occupiedList: [],
      activities: [],

      filter: {},

      hours,
      dates,
      msg: msg.availability,
      /** 館別 id → 名稱，用於時段的館別文字 */
      branchNames: {},
      /** 館別篩選；有值時不含該館別的時段不上綠底 */
      branchFilter: '',
    };
  },
  computed: {
    dayInfos() {
      return buildDayInfo(this.dates.map((d) => d.value), this.availability, this.holidays);
    },
    configured() {
      return hasWeeklySlots(this.availability);
    },
    /** 每週時段有用到的館別，依第一次出現的順序；名稱未載入的館別不列 */
    branchOptions() {
      if (!this.configured) {
        return [];
      }
      const ids = [];
      this.availability.weekly.forEach((w) => (w.branch_ids || []).forEach((id) => {
        if (!ids.includes(id) && this.branchNames[id]) {
          ids.push(id);
        }
      }));
      return ids.map((id) => ({ id, name: this.branchNames[id] }));
    },
    /** 每週時段的綠底區塊，座標公式與 CalendarBlock 一致；整天不可排案的日期不畫。
     * 館別不同的相鄰時段分開畫，未指定館別的時段不顯示館別文字，篩選時視為符合 */
    availableSlots() {
      if (!this.configured) {
        return [];
      }
      const { separator } = msg.availability.branchFilter;
      const slots = [];
      this.dayInfos.forEach((info, i) => {
        if (info.isOff) {
          return;
        }
        const day = jsDayToWeekday(this.dates[i].value.getDay());
        info.branchSlots.forEach((s) => {
          slots.push({
            key: `${info.dateKey}-${s.start}`,
            branchText: s.branchIds.map((id) => this.branchNames[id]).filter((name) => !!name).join(separator),
            dimmed: !!this.branchFilter && s.branchIds.length > 0 && !s.branchIds.includes(this.branchFilter),
            style: {
              left: `${80 + 160 * (day - 1)}px`,
              top: `${60 + s.start * 2}px`,
              height: `${(s.end - s.start) * 2}px`,
            },
          });
        });
      });
      return slots;
    },
    /** 部分不可排案時段的灰底區塊，蓋在綠底之上；整天不可排案的日期不畫（整欄已是灰底） */
    partialOffBlocks() {
      const blocks = [];
      this.dayInfos.forEach((info, i) => {
        if (info.isOff) {
          return;
        }
        const day = jsDayToWeekday(this.dates[i].value.getDay());
        info.partialOff.forEach((p, pIdx) => {
          blocks.push({
            key: `${info.dateKey}-off-${pIdx}`,
            style: {
              left: `${80 + 160 * (day - 1)}px`,
              top: `${60 + p.start * 2}px`,
              height: `${(p.end - p.start) * 2}px`,
            },
          });
        });
      });
      return blocks;
    },
    year() {
      return this.dates[0].value.getFullYear();
    },
    month() {
      return this.dates[0].value.getMonth() + 1;
    },
  },
  watch: {
    time() {
      this.changeDate();
    },
    professionalId() {
      this.loadData();
    },
    /** 換專業人士後，篩選的館別不在新的時段裡就清掉 */
    branchOptions(options) {
      if (this.branchFilter && !options.some((b) => b.id === this.branchFilter)) {
        this.branchFilter = '';
      }
    },
    availability: {
      handler(value) {
        if (hasWeeklySlots(value) && Object.keys(this.branchNames).length === 0) {
          this.loadBranchNames();
        }
      },
      immediate: true,
    },
  },
  methods: {
    /** 館別名稱只影響顯示，失敗時不提示，時段照常畫但沒有館別文字 */
    async loadBranchNames() {
      try {
        const branches = await getBranches();
        const names = {};
        branches.forEach((b) => {
          names[b.id] = b.name;
        });
        this.branchNames = names;
      } catch (e) {
        this.branchNames = {};
      }
    },
    partialOffText(info) {
      return describePartialOff(info.partialOff);
    },
    prevWeek() {
      const start = new Date(this.dates[0].value.getTime() - 86400 * 7 * 1000);
      this.dates = this.getDatesFrom(start);
      this.$emit('weekChange', this.dates);
      this.loadData();
    },
    nextWeek() {
      const start = new Date(this.dates[0].value.getTime() + 86400 * 7 * 1000);
      this.dates = this.getDatesFrom(start);
      this.$emit('weekChange', this.dates);
      this.loadData();
    },
    thisWeek() {
      this.dates = this.getDefaultDates();
      this.$emit('weekChange', this.dates);
      this.loadData();
    },
    getDefaultDates() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let shift = today.getDay();
      if (shift === 0) {
        shift = 7;
      }
      shift -= 1;
      const start = new Date(today.getTime() - shift * 86400 * 1000);
      return this.getDatesFrom(start);
    },
    getDatesFrom(start) {
      const dates = [];
      for (let i = 0; i < 7; i += 1) {
        const value = new Date(start.getTime() + 86400 * 1000 * i);
        dates.push({
          text: this.$timestampToDateWithDay(value),
          value,
        });
      }
      return dates;
    },
    getFilter() {
      const end = new Date(this.dates[this.dates.length - 1].value.getTime());
      end.setHours(23, 59, 59, 999);
      const filter = {
        start_at: parseInt(this.dates[0].value.getTime() / 1000, 10),
        end_at: parseInt(end.getTime() / 1000, 10),
      };
      if (this.professionalId) {
        filter.professional_id = this.professionalId;
      }
      return filter;
    },
    changeDate() {
      this.page = 1;
      this.$execWithLoading(async () => {
        await this.loadReserveList();
        await this.loadActivities();
      }, (e) => this.$showAxiosException('讀取行程失敗', e));
    },
    loadData() {
      if (this.emptyText) {
        this.reserveList = [];
        this.occupiedList = [];
        this.activities = [];
        return;
      }
      this.$execWithLoading(async () => {
        if (this.loader) {
          const rsp = await this.loader(this.getFilter());
          this.setReserveList(rsp.appointments || []);
          this.occupiedList = rsp.occupied || [];
          this.setActivities(rsp.activities || []);
        } else {
          await Promise.all([
            this.loadReserveList(),
            this.loadActivities(),
          ]);
        }

        this.scrollToHour(this.hour);
      }, (e) => this.$showAxiosException('讀取行程失敗', e));
    },
    async loadActivities() {
      // 專業人士站回傳陣列、縣市欄位為 county；管理站回傳 { meta, activities }、欄位為 city
      const rsp = await getActivities(this.getFilter());
      this.setActivities(rsp.activities || rsp);
    },
    setActivities(activities) {
      this.activities = JSON.parse(JSON.stringify(activities)).map((a) => ({
        ...a,
        county: a.county || a.city,
      }));
    },
    async loadReserveList() {
      // 專業人士站個案欄位為 patient，管理站為 user
      const reserveList = await getAllAppointments(this.getFilter());
      this.setReserveList(reserveList);
    },
    setReserveList(reserveList) {
      this.reserveList = JSON.parse(JSON.stringify(reserveList))
        .filter((r) => r.status !== 'cancelled')
        .map((r) => ({
          ...r,
          patient: r.patient || r.user,
        }));
    },
    scrollToHour(hour) {
      if (!this.$refs.table) {
        return;
      }
      this.$refs.table.scrollTop = 60 * 2 * hour;
    },
    showDetail(current) {
      this.$emit('showDetail', current);
    },
    showPatient(id) {
      this.$emit('showPatient', id);
    },
    showActivity(data) {
      this.$emit('showActivity', data);
    },
  },
  mounted() {
    this.$emit('weekChange', this.dates);
    this.$on('reload', () => {
      this.thisWeek();
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';
$available-slot-color: rgba($success-color, 0.08);
$calendar-default-color: #F7F8FA;
$off-color: #EBEDF0;

.view-part {
  overflow: hidden;
  position: relative;
  .view-table {
    margin-top: 20px;
    background: white;
    position: relative;
    flex: 1;
    border: 1px solid $border-base-color;
    overflow-x: auto;
    overflow-x: overlay;
    // 有設定每週時段時，時段以外的格子改為淺灰；未設定（全時段可排案）維持白色
    &.has-slots {
      background: $calendar-default-color;
      .td:not(.is-off) {
        background: $calendar-default-color;
      }
    }
  }
  .controller {
    .filter {
      font-size: 14px;
    }
    .availability-hint {
      font-size: 14px;
      color: $text-secondary-color;
      .legend-item {
        gap: 4px;
      }
      .legend-box {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 1px solid $border-base-color;
        &.available {
          background: $available-slot-color;
        }
        &.outside {
          background: $calendar-default-color;
        }
        &.off {
          background: $off-color;
        }
      }
      .holiday-text {
        color: $danger-color;
      }
    }
  }
  .empty {
    margin-top: 20px;
    background: white;
    border: 1px solid $border-base-color;
    color: $text-secondary-color;
  }
  // 標籤放在日期下方第二行，避免與日期同行時寬度不夠而跑版
  .day-tag {
    margin-top: 2px;
  }
  .date-text.is-holiday {
    color: $danger-color;
    cursor: default;
  }
  // 與管理後台行程表的專業人士選單同尺寸
  .branch-filter {
    width: 240px;
    font-size: 14px;
  }
  // 可 hover 看館別 tooltip；預約與活動區塊在後面，會蓋在上方
  .available-slot {
    position: absolute;
    background: $available-slot-color;
    width: 160px;
    overflow: hidden;
    // 不含篩選館別的時段不上底色，露出下方格子的顏色
    &.dimmed {
      background: transparent;
    }
    .branch-text {
      padding: 2px 6px;
      font-size: 12px;
      line-height: 16px;
      color: $success-color;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &.dimmed .branch-text {
      color: $text-secondary-color;
    }
  }
  .partial-off {
    position: absolute;
    background: $off-color;
    width: 160px;
    pointer-events: none;
  }
  .tr {
    flex: 0 60px;
  }
  .header {
    position: sticky;
    top: 0;
    .tr {
      background: #E6F4F4;
      .th {
        background: #E6F4F4;
        border-right: 1px solid $border-base-color;
        &:first-child {
          position: sticky;
          left: 0;
        }
      }
    }
    z-index: 2;
  }
  .body {
    .tr {
      .td {
        border-right: 1px solid $border-base-color;
        border-top: 1px solid $border-info-color;
        background: white;
        &.is-off {
          background: $off-color;
        }
        &:first-child {
          position: sticky;
          left: 0;
          z-index: 1;
        }
      }
      &:nth-child(2n) {
        .td {
          border-top: 1px solid $border-base-color;
        }
        .td:first-child {
          border-top: none;
        }
      }
    }
  }
  .time {
    flex: 0 0 80px;
    padding: 0 16px;
  }
  .room {
    flex: 0 0 160px;
    padding: 0 16px;
  }
}
</style>
